/**
 * Flow Visualization Types
 *
 * Type definitions for the Sankey-style flow visualization engine.
 * Based on the specification in docs/spec/sankey-visualization-engine.md
 *
 * WP-FLOW-6: Added expanded AccountType union and liability detection
 */

// ============================================================================
// Account Type Classification (WP-FLOW-6)
// ============================================================================

/**
 * Full account type union matching the domain model
 *
 * Assets: depository, investment, real_estate, insurance, other_asset
 * Liabilities: credit, loan, other_liability
 */
export type AccountType =
  | 'depository' // Asset - checking, savings
  | 'investment' // Asset - brokerage, retirement
  | 'real_estate' // Asset - property
  | 'insurance' // Asset - life insurance cash value
  | 'other_asset' // Asset - other assets
  | 'credit' // Liability - credit cards
  | 'loan' // Liability - mortgages, auto loans, personal loans
  | 'other_liability' // Liability - other liabilities
  | 'other'; // Legacy fallback

/**
 * Liability account types - used for visual differentiation
 */
export const LIABILITY_TYPES: readonly AccountType[] = [
  'credit',
  'loan',
  'other_liability',
];

/**
 * Type guard to check if an account is a liability
 *
 * @param accountType - The account type to check
 * @returns true if the account is a liability (credit, loan, other_liability)
 */
export function isLiabilityAccount(accountType: AccountType | string): boolean {
  return LIABILITY_TYPES.includes(accountType as AccountType);
}

/**
 * Asset account types - for reference
 */
export const ASSET_TYPES: readonly AccountType[] = [
  'depository',
  'investment',
  'real_estate',
  'insurance',
  'other_asset',
];

/**
 * Type guard to check if an account is an asset
 *
 * @param accountType - The account type to check
 * @returns true if the account is an asset
 */
export function isAssetAccount(accountType: AccountType | string): boolean {
  return ASSET_TYPES.includes(accountType as AccountType);
}

// ============================================================================
// Node Types
// ============================================================================

/**
 * Inflow node representing income sources
 */
export interface InflowNode {
  id: string; // "income-{category_id}"
  type: 'income';
  name: string;
  value: number; // Total amount for period
  color: string;
  categoryId: string;
}

/**
 * Account node representing a financial account
 */
export interface AccountNode {
  id: string; // "account-{account_id}"
  type: 'account';
  name: string;
  balance: number; // End-of-period balance (DISPLAY VALUE)
  flowTotal: number; // Transaction activity (for edge sizing)
  tier: 'A' | 'B' | 'C' | 'D';
  accountType: AccountType;
  accountId: string;
}

/**
 * Lumped node for aggregated tier B/C/D accounts
 */
export interface LumpedNode {
  id: string; // "tier-B-lumped", "tier-C-lumped", or "tier-D-lumped"
  type: 'lumped';
  name: string; // "Savings & Investments", "Other Assets", or "Other Liabilities"
  totalBalance: number;
  aggregateInflow: number;
  aggregateOutflow: number;
  tier: 'B' | 'C' | 'D';
  containedAccounts: AccountNode[];
  /** Number of accounts in this lumped node (from backend metadata, used when containedAccounts is empty) */
  accountCount?: number;
}

/**
 * Outflow node representing expense categories
 */
export interface OutflowNode {
  id: string; // "expense-{category_id}"
  type: 'expense';
  name: string;
  value: number;
  color: string;
  categoryId: string;
}

/**
 * Union type for all flow nodes
 */
export type FlowNode = InflowNode | AccountNode | LumpedNode | OutflowNode;

// ============================================================================
// Edge Types
// ============================================================================

/**
 * Edge connecting two nodes in the flow diagram
 */
export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  value: number;
  type: 'income' | 'expense' | 'transfer';
}

// ============================================================================
// Positioned Types (after layout calculation)
// ============================================================================

/**
 * Node with calculated position
 */
export interface PositionedNode {
  node: FlowNode;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Edge with calculated path
 */
export interface PositionedEdge {
  edge: FlowEdge;
  path: string;
  strokeWidth: number;
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Props for the main FlowVisualization component
 */
export interface FlowVisualizationProps {
  inflowNodes: InflowNode[];
  accountNodes: AccountNode[];
  outflowNodes: OutflowNode[];
  edges: FlowEdge[];
  expandedTiers?: string[];
  onNodeClick?: (node: FlowNode) => void;
  onEdgeClick?: (edge: FlowEdge) => void;
  className?: string;
}

/**
 * Props for FlowCanvas component
 */
export interface FlowCanvasProps {
  children: React.ReactNode;
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Props for individual node components
 */
export interface NodeProps<T extends FlowNode> {
  node: T;
  x: number;
  y: number;
  width: number;
  height: number;
  onClick?: (node: T) => void;
}

/**
 * Props for edge components
 */
export interface EdgeProps {
  edge: FlowEdge;
  path: string;
  strokeWidth: number;
  onClick?: (edge: FlowEdge) => void;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if a node is an inflow node
 */
export function isInflowNode(node: FlowNode): node is InflowNode {
  return node.type === 'income';
}

/**
 * Check if a node is an account node
 */
export function isAccountNode(node: FlowNode): node is AccountNode {
  return node.type === 'account';
}

/**
 * Check if a node is a lumped node
 */
export function isLumpedNode(node: FlowNode): node is LumpedNode {
  return node.type === 'lumped';
}

/**
 * Check if a node is an outflow node
 */
export function isOutflowNode(node: FlowNode): node is OutflowNode {
  return node.type === 'expense';
}

// ============================================================================
// Transfer Types (WP-TRANSFER-2)
// ============================================================================

/**
 * Transfer type classification
 * - paired: Both source and target accounts are tracked
 * - single_outgoing: Only source is tracked, target is external/untracked
 * - single_incoming: Only target is tracked, source is external/untracked
 */
export type TransferType = 'paired' | 'single_outgoing' | 'single_incoming';

/**
 * Transfer data representing money movement between accounts
 * Displayed in the separate TransferSection below the main flow diagram
 *
 * WP-TRANSFER-2: Updated to support single-sided transfers where one
 * account is external/untracked (shown as "[External]" placeholder)
 */
export interface TransferData {
  id: string;
  /** Source account ID, null for single_incoming (external source) */
  sourceAccountId: string | null;
  /** Source account name, "[External]" for single_incoming */
  sourceAccountName: string;
  /** Target account ID, null for single_outgoing (external target) */
  targetAccountId: string | null;
  /** Target account name, "[External]" for single_outgoing */
  targetAccountName: string;
  /** Transfer amount (always positive) */
  amount: number;
  /** Transfer subcategory (e.g., "Account Transfer", "Debt Payment") */
  subcategory: string;
  /** Transfer type classification */
  transferType: TransferType;
  /** List of transaction IDs that make up this transfer */
  transactionIds: string[];
}

/**
 * Transfer section data from backend API (WP-TRANSFER-2)
 * Contains all transfers for display below the main Sankey diagram
 */
export interface TransferSectionData {
  transfers: TransferData[];
  totalCount: number;
  totalAmount: number;
}

// ============================================================================
// Transfer Sankey Types (WP-FLOW-REFACTOR-2)
// ============================================================================

/**
 * Node in the transfer Sankey diagram
 * Represents either a source (sending) or target (receiving) account
 */
export interface TransferSankeyNode {
  /** Unique identifier for the node */
  id: string;
  /** Node type classification */
  type: 'transfer_source' | 'transfer_target' | 'external';
  /** Display name for the node */
  name: string;
  /** Account ID (null for external nodes) */
  accountId: string | null;
  /** Total amount flowing through this node */
  totalAmount: number;
  /** Whether this represents an external/untracked account */
  isExternal: boolean;
}

/**
 * Edge connecting source and target nodes in the transfer Sankey
 */
export interface TransferSankeyEdge {
  /** Unique identifier for the edge */
  id: string;
  /** Source node ID */
  sourceNodeId: string;
  /** Target node ID */
  targetNodeId: string;
  /** Amount transferred */
  amount: number;
  /** List of transaction IDs that make up this edge */
  transactionIds: string[];
  /** Number of transactions in this edge */
  transactionCount: number;
}

/**
 * Complete transfer Sankey data from the API
 */
export interface TransferSankeyData {
  /** Source nodes (left column - accounts sending money) */
  sourceNodes: TransferSankeyNode[];
  /** Target nodes (right column - accounts receiving money) */
  targetNodes: TransferSankeyNode[];
  /** Edges connecting source to target nodes */
  edges: TransferSankeyEdge[];
  /** Total amount of all transfers */
  totalAmount: number;
  /** Total number of transfers */
  totalCount: number;
}
